import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Repeat, X } from "lucide-react";
import { useVehicles, vehicleName } from "@/lib/useVehicles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REASONS = ["Customer preference", "Maintenance", "Mechanical issue", "Upgrade", "Other"];

export default function SwapCar({ booking, token, onSaved }) {
  const { vehicles } = useVehicles();
  const [open, setOpen] = useState(false);
  const [newVehicle, setNewVehicle] = useState("");
  const [reason, setReason] = useState("Customer preference");
  const [toMaint, setToMaint] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const options = vehicles.filter((v) => vehicleName(v) !== booking.vehicle);

  const submit = async () => {
    if (!newVehicle) { toast.error("Pick a vehicle to swap to."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/swap`,
        { new_vehicle: newVehicle, reason, send_to_maintenance: toMaint, maintenance_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } });
      let msg = `Swapped to ${data.new_vehicle}.`;
      if (data.sent_to_maintenance) msg += " Old car sent to maintenance & mechanic notified.";
      if (data.agreement_stale) msg += " ⚠ Re-issue the agreement.";
      toast.success(msg);
      setOpen(false); setNewVehicle(""); setReason("Customer preference"); setToMaint(false); setNotes("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not swap the car.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button data-testid={`swap-car-btn-${booking.id}`} onClick={() => setOpen(true)}
        className="text-xs flex items-center gap-1.5 border border-blue-400/40 text-blue-300 px-3 py-1.5 rounded hover:bg-blue-500 hover:text-white transition-colors">
        <Repeat size={13} /> Swap Car
      </button>
    );
  }
  return (
    <div data-testid={`swap-car-form-${booking.id}`} className="w-full mt-2 rounded-lg border border-blue-400/30 bg-[#0A0A0A] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-blue-300 flex items-center gap-1.5"><Repeat size={14} /> Swap from <b className="text-white">{booking.vehicle}</b></span>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select data-testid={`swap-new-vehicle-${booking.id}`} value={newVehicle} onChange={(e) => setNewVehicle(e.target.value)}
          className="bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-400/50">
          <option value="">Swap to…</option>
          {options.map((v) => <option key={v.id} value={vehicleName(v)}>{v.year} {vehicleName(v)}{v.status !== "available" ? ` (${v.status})` : ""}</option>)}
        </select>
        <select data-testid={`swap-reason-${booking.id}`} value={reason} onChange={(e) => setReason(e.target.value)}
          className="bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-400/50">
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" data-testid={`swap-to-maintenance-${booking.id}`} checked={toMaint} onChange={(e) => setToMaint(e.target.checked)} className="h-4 w-4 accent-blue-400" />
        <span className="text-xs text-white/60">Send old car to maintenance &amp; text the mechanic</span>
      </label>
      {toMaint && (
        <input data-testid={`swap-maint-notes-${booking.id}`} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="What's wrong? (sent to mechanic)" className="w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-blue-400/50" />
      )}
      <div className="flex justify-end">
        <button data-testid={`swap-submit-${booking.id}`} onClick={submit} disabled={busy}
          className="bg-blue-500 text-white font-semibold text-sm px-4 py-2 rounded hover:bg-blue-600 transition-colors disabled:opacity-60 flex items-center gap-1.5">
          {busy && <Loader2 className="animate-spin" size={14} />} Swap Car
        </button>
      </div>
      <p className="text-white/35 text-xs">Keeps the agreed total. Blocks if the new car is already booked for these dates.</p>
    </div>
  );
}
